import { useState } from 'react';
import type { ConflictRecord } from '../storage/conflictRepository';
import { resolveConflict, type Resolution } from '../storage/cloudSync';
import { formatBytes } from '../storage/romRepository';
import { useStore } from '../state/store';

export interface SyncConflictDialogProps {
  conflicts: ConflictRecord[];
  onClose: () => void;
  onResolved: () => void;
}

/**
 * 同期の競合をユーザーに解決してもらうダイアログ。
 *
 * 自動でどちらかを採用することは意図的にしていない。
 * セーブデータが黙って消えるのはこのアプリで最悪の不具合なので、
 * 「どちらが新しいか」を提示したうえで判断は必ず人に委ねる。
 */
export function SyncConflictDialog({ conflicts, onClose, onResolved }: SyncConflictDialogProps) {
  const toast = useStore((s) => s.toast);
  const [busy, setBusy] = useState<string | null>(null);

  const handle = async (conflict: ConflictRecord, resolution: Resolution) => {
    setBusy(conflict.id);
    try {
      await resolveConflict(conflict.id, resolution);
      toast(
        resolution === 'local'
          ? 'この端末のデータでサーバーを上書きしました'
          : resolution === 'remote'
            ? 'サーバーのデータを取り込みました'
            : '両方を残しました',
        'success',
      );
      onResolved();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center">
      <div className="animate-fade-in flex max-h-[85vh] w-full max-w-lg flex-col rounded-t-3xl border-t border-base-600 bg-base-800 sm:rounded-3xl sm:border">
        <header className="shrink-0 border-b border-base-700 p-4">
          <h2 className="text-sm font-semibold">同期の競合 ({conflicts.length} 件)</h2>
          <p className="mt-1 text-[0.7rem] leading-relaxed text-slate-400">
            この端末とサーバーで、同じセーブが別々に更新されています。
            どちらを残すか選んでください。選ぶまでどちらも削除されません。
          </p>
        </header>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          {conflicts.map((c) => (
            <ConflictCard
              key={c.id}
              conflict={c}
              busy={busy === c.id}
              onResolve={(r) => void handle(c, r)}
            />
          ))}
        </div>

        <footer className="shrink-0 border-t border-base-700 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <button type="button" className="btn-ghost w-full" onClick={onClose}>
            あとで決める
          </button>
        </footer>
      </div>
    </div>
  );
}

function ConflictCard({
  conflict,
  busy,
  onResolve,
}: {
  conflict: ConflictRecord;
  busy: boolean;
  onResolve: (r: Resolution) => void;
}) {
  const localNewer = conflict.localUpdatedAt >= conflict.remoteUpdatedAt;
  const label =
    conflict.kind === 'sram' ? 'セーブデータ (SRAM)' : `セーブステート スロット ${conflict.slot + 1}`;

  return (
    <div className="rounded-xl border border-base-600 bg-base-900 p-3">
      <p className="truncate text-xs font-semibold">{conflict.romTitle}</p>
      <p className="mt-0.5 text-[0.65rem] text-slate-500">{label}</p>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <SideCard
          title="この端末"
          recommended={localNewer}
          updatedAt={conflict.localUpdatedAt}
          size={conflict.localSize}
          thumbnail={conflict.localThumbnail}
        />
        <SideCard
          title="サーバー"
          recommended={!localNewer}
          updatedAt={conflict.remoteUpdatedAt}
          size={conflict.remoteSize}
        />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => onResolve('local')}
          className="btn-ghost !min-h-0 !py-2 !text-xs"
        >
          この端末を残す
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onResolve('remote')}
          className="btn-ghost !min-h-0 !py-2 !text-xs"
        >
          サーバーを残す
        </button>
      </div>

      {/* SRAM はスロットの概念が無いので「両方残す」は出せない */}
      {conflict.kind === 'state' && (
        <button
          type="button"
          disabled={busy}
          onClick={() => onResolve('keep-both')}
          className="btn-ghost mt-2 w-full !min-h-0 !py-2 !text-xs"
        >
          両方残す（サーバー側を空きスロットへ）
        </button>
      )}
    </div>
  );
}

function SideCard({
  title,
  recommended,
  updatedAt,
  size,
  thumbnail,
}: {
  title: string;
  recommended: boolean;
  updatedAt: number;
  size: number;
  thumbnail?: string | undefined;
}) {
  return (
    <div
      className={`rounded-lg border p-2 ${
        recommended ? 'border-accent/50 bg-accent/10' : 'border-base-600 bg-base-800'
      }`}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="text-[0.65rem] font-medium">{title}</span>
        {recommended && <span className="text-[0.6rem] text-accent">新しい</span>}
      </div>

      {thumbnail && (
        <img
          src={thumbnail}
          alt=""
          className="mt-1.5 h-12 w-full rounded object-cover"
          style={{ imageRendering: 'pixelated' }}
        />
      )}

      <p className="mt-1.5 text-[0.6rem] leading-relaxed text-slate-500">
        {updatedAt > 0 ? new Date(updatedAt).toLocaleString('ja-JP') : '日時不明'}
        <br />
        {formatBytes(size)}
      </p>
    </div>
  );
}
